import {
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { LockerMigrationState } from "./lockerMigrationOverride"

const STATE_ORDER: LockerMigrationState[] = [
  "active",
  "superseded",
  "pendingActivation",
]

/**
 * Target-state picker as selectable cards.
 *
 * Still a real `RadioGroup` underneath rather than clickable divs — arrow-key
 * navigation and the radio role come free, and the card is only styling on top
 * of the label, so the whole row stays one hit target.
 */
export const SaLockerMigrationStateOptions: FC<{
  value: LockerMigrationState
  currentState: LockerMigrationState
  onChange: (next: LockerMigrationState) => void
}> = ({ value, currentState, onChange }) => {
  const { t } = useTranslation()

  return (
    <RadioGroup
      value={value}
      onChange={(e) => onChange(e.target.value as LockerMigrationState)}
    >
      <Stack direction="column" spacing={1}>
        {STATE_ORDER.map((s) => {
          const selected = s === value
          return (
            <Box
              key={s}
              sx={{
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: selected ? "primary.main" : "divider",
                bgcolor: selected ? "action.hover" : "transparent",
                transition: "border-color 120ms, background-color 120ms",
                "&:hover": { borderColor: selected ? "primary.main" : "text.disabled" },
              }}
            >
              <FormControlLabel
                value={s}
                sx={{ m: 0, px: 1, py: 1, width: "100%", alignItems: "flex-start" }}
                control={<Radio size="small" sx={{ py: 0.25 }} />}
                label={
                  <Box sx={{ pt: 0.25 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                        {t(`lockerMigrationOverride.state.${s}`)}
                      </Typography>
                      {s === currentState && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t("lockerMigrationOverride.currentBadge")}
                          sx={{ height: 17, fontSize: "0.62rem" }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t(`lockerMigrationOverride.stateHint.${s}`)}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )
        })}
      </Stack>
    </RadioGroup>
  )
}

export default SaLockerMigrationStateOptions
